// /src/pages/AIAgent.js
import { useState, useEffect } from "react";
import { Bot, Zap, Users, PhoneCall } from "lucide-react";

const RULES = [
  "Follow up new leads after 1 hour",
  "Re-engage stale leads after 3 days",
  "Send proposal reminder after 48 hours",
  "Win/loss reason collection",
];

function getToken() { return localStorage.getItem("token"); }

export default function AIAgent() {
  const [toggles, setToggles] = useState(RULES.map(() => false));
  const [stats, setStats] = useState({ active: 0, qualified: 0, callsOffered: 0 });
  const flip = (i) => setToggles(prev => prev.map((v, idx) => idx === i ? !v : v));

  useEffect(() => {
    fetch("/api/fbconversations", { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.json())
      .then(convs => {
        if (!Array.isArray(convs)) return;
        setStats({
          active: convs.filter(c => c.stage !== "call_offered" && c.stage !== "disqualified").length,
          qualified: convs.filter(c => c.stage === "qualified" || c.stage === "call_offered").length,
          callsOffered: convs.filter(c => c.stage === "call_offered").length,
        });
      })
      .catch(() => {});
  }, []);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">AI Follow-up Agent</div>
          <div className="page-subtitle">Alex automatically qualifies leads and books calls via Messenger.</div>
        </div>
        <span className="badge badge-green" style={{ alignSelf: "center" }}>Active</span>
      </div>

      <div className="agent-banner">
        <Bot size={24} color="#2563eb" />
        <div className="agent-banner-text">
          <strong>Alex is running</strong>
          <span>New leads get an automatic opening message. Replies are handled by AI until a call is booked.</span>
        </div>
        <span className="badge badge-blue">Groq · Llama 3.1</span>
      </div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 16 }}>
        {[
          { label: "Active conversations", value: stats.active, icon: <Users size={16} color="#2563eb" /> },
          { label: "Leads qualified", value: stats.qualified, icon: <Zap size={16} color="#16a34a" /> },
          { label: "Calls offered", value: stats.callsOffered, icon: <PhoneCall size={16} color="#9333ea" /> },
        ].map(s => (
          <div key={s.label} className="card" style={{ padding: "16px 20px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
              {s.icon}
              <span style={{ fontSize: 12, color: "#64748b" }}>{s.label}</span>
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: "#0f172a" }}>{s.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div className="card">
          <div className="card-header"><span className="card-title">⚡ Follow-up Rules</span></div>
          <div className="card-body">
            <div className="agent-rules">
              {RULES.map((rule, i) => (
                <div key={i} className="agent-rule">
                  <label>{rule}</label>
                  <button className={`toggle ${toggles[i] ? "on" : ""}`} onClick={() => flip(i)} />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header"><span className="card-title">🗣️ Agent Persona</span></div>
          <div className="card-body" style={{ fontSize: 13, color: "#475569", display: "flex", flexDirection: "column", gap: 10 }}>
            <div><strong style={{ color: "#0f172a" }}>Name:</strong> Alex</div>
            <div><strong style={{ color: "#0f172a" }}>Role:</strong> Loan specialist assistant</div>
            <div><strong style={{ color: "#0f172a" }}>Channel:</strong> Facebook Messenger</div>
            <div><strong style={{ color: "#0f172a" }}>Model:</strong> Llama 3.1 8b (Groq)</div>
            <div><strong style={{ color: "#0f172a" }}>Qualifies on:</strong> Project type, credit 650+, LLC status, property identified</div>
            <div><strong style={{ color: "#0f172a" }}>On qualify:</strong> Books call via Calendly</div>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-header"><span className="card-title">Outreach Channels</span></div>
        <div className="card-body">
          <div className="channels-grid">
            {[
              { name: "Messenger", emoji: "📘", status: "Active" },
              { name: "WhatsApp", emoji: "💬", status: "Not connected" },
              { name: "SMS (Twilio)", emoji: "📱", status: "Not connected" },
              { name: "Email", emoji: "📧", status: "Not connected" },
            ].map(ch => (
              <div key={ch.name} className="channel-card">
                <span className="channel-emoji">{ch.emoji}</span>
                <div className="channel-name">{ch.name}</div>
                <div className="channel-status" style={{ color: ch.status === "Active" ? "#16a34a" : undefined }}>
                  {ch.status}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}